/**
 * Projects Management Hook
 * 
 * Manages project entities that serve as containers for organizing related jobs.
 * Projects help clients group and track multiple jobs, providing hierarchical
 * organization and aggregated metrics across job portfolios.
 * 
 * **Project Features:**
 * - Group related jobs under common projects
 * - Track project-level metrics and status
 * - Reorder projects via drag-and-drop
 * - Color coding for visual organization
 * - Description and date tracking
 * 
 * **Automatic Metrics:**
 * - Job count per project
 * - Status breakdown (draft, awarded, completed, etc.)
 * - Total budget calculation across all jobs
 * - Completion percentage
 * 
 * **Project Lifecycle:**
 * 1. Create project with name, description, color
 * 2. Add jobs to project (via `useProjectJobs` hook)
 * 3. Monitor project metrics
 * 4. Update project details as needed
 * 5. Delete project (orphans jobs but doesn't delete them)
 * 
 * **Display Ordering:**
 * - Projects have `display_order` for custom sorting
 * - Reorder via drag-and-drop interface
 * - Lower numbers appear first
 * 
 * **Key Features:**
 * - Fetch all projects with metrics (auto-loads)
 * - Create new projects
 * - Update project details
 * - Delete projects (with job count validation)
 * - Reorder projects
 * 
 * **Metrics Calculation:**
 * - Fetches related jobs for each project
 * - Calculates status breakdown
 * - Sums budgets across all jobs
 * - All metrics updated on project fetch
 * 
 * @returns {Object} Projects management
 * @returns {ProjectWithMetrics[]} projects - Array of projects with metrics
 * @returns {boolean} loading - Loading state
 * @returns {string | null} error - Error message if operation failed
 * @returns {Function} createProject - Create new project
 * @returns {Function} updateProject - Update existing project
 * @returns {Function} deleteProject - Delete project (validates no jobs)
 * @returns {Function} reorderProject - Update project display order
 * 
 * @example
 * // Fetch and display projects
 * const { projects, loading } = useProjects();
 * 
 * projects.map(project => (
 *   <ProjectCard
 *     key={project.id}
 *     name={project.name}
 *     jobCount={project.job_count}
 *     color={project.color}
 *     status={project.status_breakdown}
 *   />
 * ));
 * 
 * @example
 * // Create new project
 * const { createProject } = useProjects();
 * 
 * await createProject({
 *   name: 'Q1 2024 Infrastructure',
 *   description: 'Networking and cabling upgrades',
 *   color: '#3B82F6',
 *   target_completion_date: '2024-03-31'
 * });
 * 
 * @example
 * // Update project
 * const { updateProject } = useProjects();
 * 
 * await updateProject(projectId, {
 *   name: 'Updated Project Name',
 *   description: 'New description'
 * });
 * 
 * @example
 * // Reorder projects (drag-and-drop)
 * const { reorderProject } = useProjects();
 * 
 * await reorderProject(projectId, newDisplayOrder);
 * 
 * @example
 * // Delete project (fails if has jobs)
 * const { deleteProject } = useProjects();
 * 
 * try {
 *   await deleteProject(projectId);
 * } catch (error) {
 *   // Error if project has associated jobs
 *   console.error('Cannot delete project with jobs');
 * }
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Project, ProjectFormData, ProjectWithMetrics } from '@/lib/types/project';
import { toast } from 'sonner';

export function useProjects() {
  const [projects, setProjects] = useState<ProjectWithMetrics[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch projects
      const { data: projectsData, error: projectsError } = await supabase
        .from('projects')
        .select('*')
        .order('display_order', { ascending: true })
        .order('created_at', { ascending: false });

      if (projectsError) throw projectsError;

      // Fetch job counts and metrics for each project
      const projectsWithMetrics: ProjectWithMetrics[] = await Promise.all(
        (projectsData || []).map(async (project) => {
          const { data: jobs, error: jobsError } = await supabase
            .from('jobs')
            .select('status, budget_cents')
            .eq('project_id', project.id);

          if (jobsError) {
            console.error('Error fetching jobs for project:', jobsError);
            return {
              ...project,
              job_count: 0,
              status_breakdown: {
                draft: 0,
                rfq_open: 0,
                awarded: 0,
                in_progress: 0,
                completed_pending_review: 0,
                accepted: 0,
                rejected: 0,
                disputed: 0,
                closed: 0,
              },
            } as ProjectWithMetrics;
          }

          const status_breakdown = jobs.reduce((acc, job) => {
            const status = job.status as keyof typeof acc;
            acc[status] = (acc[status] || 0) + 1;
            return acc;
          }, {
            draft: 0,
            rfq_open: 0,
            awarded: 0,
            in_progress: 0,
            completed_pending_review: 0,
            accepted: 0,
            rejected: 0,
            disputed: 0,
            closed: 0,
          });

          const total_budget_cents = jobs.reduce((sum, job) => {
            return sum + (job.budget_cents || 0);
          }, 0);

          return {
            ...project,
            job_count: jobs.length,
            status_breakdown,
            total_budget_cents: total_budget_cents > 0 ? total_budget_cents : undefined,
          } as ProjectWithMetrics;
        })
      );

      setProjects(projectsWithMetrics);
    } catch (err: any) {
      console.error('Error fetching projects:', err);
      setError(err.message);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const createProject = async (data: ProjectFormData) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: profile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('user_id', userData.user.id)
        .single();

      if (!profile?.organization_id) throw new Error('No organization found');

      const { data: project, error } = await supabase
        .from('projects')
        .insert({
          ...data,
          organization_id: profile.organization_id,
          created_by_user_id: userData.user.id,
        })
        .select()
        .single();

      if (error) throw error;

      toast.success('Project created successfully');
      await fetchProjects();
      return project;
    } catch (err: any) {
      console.error('Error creating project:', err);
      toast.error(err.message || 'Failed to create project');
      throw err;
    }
  };

  const updateProject = async (id: string, data: Partial<ProjectFormData>) => {
    try {
      const { error } = await supabase
        .from('projects')
        .update(data)
        .eq('id', id);

      if (error) throw error;

      toast.success('Project updated successfully');
      await fetchProjects();
    } catch (err: any) {
      console.error('Error updating project:', err);
      toast.error(err.message || 'Failed to update project');
      throw err;
    }
  };

  const deleteProject = async (id: string) => {
    try {
      // Check if project has jobs
      const { data: jobs, error: jobsError } = await supabase
        .from('jobs')
        .select('id')
        .eq('project_id', id);

      if (jobsError) throw jobsError;

      if (jobs && jobs.length > 0) {
        throw new Error('Cannot delete project with existing jobs. Please remove all jobs first.');
      }

      const { error } = await supabase
        .from('projects')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast.success('Project deleted successfully');
      await fetchProjects();
    } catch (err: any) {
      console.error('Error deleting project:', err);
      toast.error(err.message || 'Failed to delete project');
      throw err;
    }
  };

  const reorderProject = async (projectId: string, newOrder: number) => {
    try {
      const { error } = await supabase
        .from('projects')
        .update({ display_order: newOrder })
        .eq('id', projectId);

      if (error) throw error;

      await fetchProjects();
    } catch (err: any) {
      console.error('Error reordering project:', err);
      toast.error('Failed to reorder project');
      throw err;
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  return {
    projects,
    loading,
    error,
    fetchProjects,
    createProject,
    updateProject,
    deleteProject,
    reorderProject,
  };
}
